import * as React from 'react'
import styled from 'styled-components'
import { colors } from '@components/shared/colors'
import { Col, config, Container, Row } from 'react-awesome-styled-grid'
import type { AppStateType } from '@reducers/appstate'
import type { Dispatch } from 'redux'
import { openSubscribeConfirmed } from '../../../actions/navigationActions'
import { withRouter } from 'next/router'
import { connect, useSelector } from 'react-redux'
import { GoodSansB1, H3, P, QueensH3 } from '@components/shared/typography'
import { CTAPrimaryOrange, GlobalStyle } from '@components/shared'
import { DownArrow } from '@components/shared/icons/DownArrow'
import HtmlHead from '@components/HtmlHead'
import SimpleNav from '@components/SimpleNav'
import { TopBanner } from '@components/shared/TopBanner'
import ChoosePlan from '@components/subscribe/ChoosePlan'
import PromoCode from '@components/PromoCode'
import AuthForm from '@components/auth/AuthForm'
import { Elements } from '@stripe/react-stripe-js'
import { stripe } from '../../../services/stripe'
import BillingInformation from '@components/subscribe/BillingInformation'
import StoreAvailability from '@components/Availability'
import BackgroundGradient from '@components/BackgroundGradient'
import SubscribeFaqs from '@components/subscribe/SubscribeFlow2/SubscribeFaqs'
import { Confirmation } from '@components/subscribe/Confirmation'
import { useSearchParams } from 'next/navigation'
import type { SubscriptionTextType } from '../../../flowTypes'
import ThemeContext from '@components/shared/ThemeContext'

type PropsType = {
  loggedIn: boolean,
  isSubscribed: boolean,
  isMonthly: boolean,
  headerArray: string[],
  isTrial: boolean,
  welcomeBannerText?: string,
  bannerArray?: string[],
  featureAnnualOnly: boolean,
  subscription_expires_at?: number,
  isPrideBanner: boolean,
  isInLine?: boolean,
  dontDisplayPromo?: boolean,
  confirmationInline?: boolean,
  bannerBackgroundColor?: string,
  buttonColor?: string,
  combinedLayout?: boolean
}

type DipatchPropsType = {
  openSubscribeConfirmed: () => void
}

export function SubscribeFlow2 (props: PropsType & DipatchPropsType): React.ReactNode {
  const [showCreateAccount, setShowCreateAccount] = React.useState<boolean>(false)
  const [showInLineConfirm, setShowInLineConfirm] = React.useState<boolean>(false)
  const searchParams = useSearchParams()
  const theme = React.useContext(ThemeContext)
  const subProps = useSelector((state: AppStateType): SubscriptionTextType => state.subscriptionText)

  if (typeof document !== 'undefined') {
    const body = document.querySelector('body')
    if (body && body.style && body.style.overflow === 'hidden') {
      body.style.overflow = 'auto'
    }
  }
  React.useEffect((): void => {
    if (props.isSubscribed && !props.subscription_expires_at) {
      if (props.isInLine && props.confirmationInline !== false) {
        setShowInLineConfirm(true)
      } else {
        props.openSubscribeConfirmed()
      }
    }
  }, [props.isSubscribed, props.subscription_expires_at])

  React.useEffect((): void => {
    if (searchParams.get('socialAuth')) {
      setShowCreateAccount(true)
    }
  }, [searchParams])

  if (showInLineConfirm) {
    return <Confirmation />
  }

  let headerText = ''
  if (props.headerArray) {
    props.headerArray.forEach((text: string, index: number): void => {
      const string = text.trim()
      if (!string.length) {
        return
      }
      if (index === 1) {
        headerText = headerText + `<span>${string}</span><br/>`
      } else {
        headerText = headerText + string + `<br/>`
      }
    })
  }

  let bannerText = ''

  if (props.bannerArray) {
    props.bannerArray.forEach((text: string, index: number): void => {
      const string = text.trim()
      if (index === 1) {
        bannerText = bannerText + `<span>${string}</span> `
      } else {
        bannerText = bannerText + string + ' '
      }
    })

    if (props.welcomeBannerText) {
      bannerText = `${props.welcomeBannerText.trim()} ` + bannerText
    }
  }

  let bannerBackground = 'unset'
  if (props.isPrideBanner) {
    bannerBackground = 'linear-gradient(90deg, #29187F 0%, #251231 25.35%, #960049 51.81%, #F0432D 77.99%, #FF883E 100%);'
  }
  let backgroundColor
  if (props.bannerBackgroundColor) {
    backgroundColor = props.bannerBackgroundColor
  } else if (props.buttonColor) {
    backgroundColor = props.buttonColor
  } else if (theme === 'pink') {
    backgroundColor = colors.pink
  }
  const isLoginStep = props.loggedIn
  const isBillingStep = (props.loggedIn && (!props.isSubscribed || props.subscription_expires_at))
  return (
    <ContainerDiv>
      <GlobalStyle backgroundColor={colors.midnightBlue} />
      <HtmlHead
        title={'Dipsea | Subscribe'}
        description={
          'Get access to the entire Dipsea library to explore 1000+ sexy audio stories, soothing sleep scenes, and steamy POV role-plays.'
        }
includeStripe={false}
        canonicalUrl={'https://www.dipseastories.com/subscribe'}
      />
      {!props.isInLine && (
        <SimpleNav
          backgroundColor={colors.midnightBlue90}
          buttonOrder={['listen-white']}
        />
      )}

      {!!bannerText && (!showCreateAccount) && !props.loggedIn && (
        <TopBanner bannerText={bannerText} isPositionSticky={true} background={bannerBackground} backgroundColor={backgroundColor} />
      )}

      {!props.isInLine && <BackgroundGradient quadrant={4} />}

      <Container>
        <Row>
          {/* Combined layout: pricing + auth on same page */}
          {props.combinedLayout && !isBillingStep && <CombinedLayoutCol align={'center'}>
            <CombinedHeader dangerouslySetInnerHTML={{ __html: headerText }} />
            <GlassPricingCard>
              <ChoosePlan />
            </GlassPricingCard>
            <PromoCode theme={'white'} bottomBorder={false} dontDisplayPromo={true}/>
            {!isLoginStep && (
              <CombinedAuthSection>
                <AuthForm theme={theme === 'pink' ? 'pink' : 'white'} isCenteredAligned createAccountHeader={' '}/>
              </CombinedAuthSection>
            )}
            <StepContainer>
              <Availability />
              <TermsText>
                <a
                  style={{ color: colors.tintOrange }}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.dipseastories.com/terms/dark"
                >
                  Terms & Conditions
                </a>
                <a
                  style={{ color: colors.tintOrange }}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.dipseastories.com/privacypolicy/dark"
                >
                  Privacy Policy
                </a>
                <a
                  style={{ color: colors.tintOrange }}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.dipseastories.com/paymentterms/dark/"
                >
                  Payment Terms
                </a>
              </TermsText>
            </StepContainer>
          </CombinedLayoutCol>}

          {/* Combined layout: billing step (after auth) */}
          {props.combinedLayout && isBillingStep && <CombinedLayoutCol align={'center'}>
            <StepContainer>
              {/* Mount PromoCode here too so the URL promo applies for users who
                  land directly in the billing step (already logged in). Without
                  this, the submit button has no label because subscriptionText
                  is never populated. */}
              <PromoCode theme={'white'} bottomBorder={false} dontDisplayPromo={true}/>
              <CreateAccountDiv>
                <StepHeaderContainer>
                  <H3 width='100%' color={colors.white}>
                    Enter your billing info
                  </H3>
                </StepHeaderContainer>
                {stripe()
                  ? (
                    <Elements stripe={stripe()}>
                      <BillingInformation />
                    </Elements>
                    )
                  : (
                    <div style={{ padding: '20px', textAlign: 'center', color: colors.white }}>
                      <p>Payment processing is temporarily unavailable.</p>
                      <p>Please try again later or contact support.</p>
                    </div>
                    )}
              </CreateAccountDiv>
            </StepContainer>
          </CombinedLayoutCol>}

          {/* Default layout: step 1 (pricing) */}
          {!props.combinedLayout && !showCreateAccount && <Col
            xs={4}
            sm={8}
            md={6}
            lg={12}
            align={'center'}
            offset={{ md: 1, lg: 0 }}
          >
            <SubscribeFeaturesBlock>
              <Header dangerouslySetInnerHTML={{ __html: headerText }} $theme={theme}/>
              <Subheader>
                {subProps.description}
              </Subheader>
            </SubscribeFeaturesBlock>
            <StepContainer>
              <ChoosePlan />
              <PromoCode theme={'white'} bottomBorder={false} dontDisplayPromo={true}/>
              <SubscribeCTAButton $buttonColor={props.buttonColor} className={'subscribe-cta'} onClick={(): void => setShowCreateAccount(true)}>{subProps.ctaText}</SubscribeCTAButton>
              <Availability />
              <TermsText>
                <a
                  style={{
                    color: theme === 'pink' ? colors.white : colors.tintOrange
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.dipseastories.com/terms/dark"
                >
                  Terms & Conditions
                </a>
                <a
                  style={{
                    color: theme === 'pink' ? colors.white : colors.tintOrange
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.dipseastories.com/privacypolicy/dark"
                >
                  Privacy Policy
                </a>

                <a
                  style={{
                    color: theme === 'pink' ? colors.white : colors.tintOrange
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.dipseastories.com/paymentterms/dark/"
                >
                  Payment Terms
                </a>
              </TermsText>
            </StepContainer>
          </Col>}

          {/* Default layout: step 2 (auth or billing) */}
          {!props.combinedLayout && showCreateAccount && <Col
            align={'center'}
          >
            <StepContainer>
              {!isLoginStep && (
                <CreateAccountDiv>
                  <AuthForm theme={theme === 'pink' ? 'pink' : 'white'} isCenteredAligned/>
                </CreateAccountDiv>
              )}
              {isBillingStep && (
                <CreateAccountDiv>
                  <StepHeaderContainer>
                    <H3 width='100%' color={colors.white}>
                      Enter your billing info
                    </H3>
                  </StepHeaderContainer>
                  {stripe()
                    ? (
                      <Elements stripe={stripe()}>
                        <BillingInformation />
                      </Elements>
                      )
                    : (
                      <div style={{ padding: '20px', textAlign: 'center', color: colors.white }}>
                        <p>Payment processing is temporarily unavailable.</p>
                        <p>Please try again later or contact support.</p>
                      </div>
                      )}
                </CreateAccountDiv>
              )}
            </StepContainer>
          </Col>}
        </Row>
      </Container>
      {!showCreateAccount && !props.isInLine && <SubscribeFaqs setShowCreateAccount={setShowCreateAccount}/>}
    </ContainerDiv>
  )
}

const mapStateToProps = (state: AppStateType): PropsType => {
  const featurePride = state?.subscriptionPromo?.userEnteredPromo === 'pride2023' || state?.subscriptionPromo?.userEnteredPromo === 'PRIDE2023'
  let welcomeBannerText = state.subscriptionPromo?.welcomeBannerText || undefined
  let bannerArray = state.subscriptionText?.promoBannerText
  if (featurePride) {
    welcomeBannerText = undefined
    bannerArray = ['Happy Pride! Treat yourself to hundreds of queer sexy stories for 50% Off.', '', '']
  }

  return {
    loggedIn: !!(state.user || {}).id,
    isSubscribed: !!(state.user || {}).isSubscribed,
    subscription_expires_at: (state.user || {}).subscription_expires_at,
    isMonthly: (state.subscriptionText || {}).plan === 'monthly',
    headerArray: state.subscriptionText?.promoHeaderText || [
      'Get Unlimited Access',
      'to Dipsea'
    ],
    isTrial: !!(state.subscriptionText || {}).trial,
    bannerArray,
    welcomeBannerText,
    featureAnnualOnly: false,
    isPrideBanner: featurePride
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): DipatchPropsType => {
  return {
    openSubscribeConfirmed: (): void => dispatch(openSubscribeConfirmed(true))
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SubscribeFlow2)
) as any

const ContainerDiv = styled.div<any>`
  width: 100%;
`

const Availability = (): React.ReactNode => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  return (
    <>
      <MobileBenefitsBlock>
        <ViewSubscriberBenefitsBlock onClick={(): void => setIsOpen(!isOpen)}>
          <P
            fontSize={'14px'}
            lineHeight={'21px'}
            styles={'margin-right: 8px;'}
          >
            View availability
          </P>
          <DownArrow size={15} color={colors.white} reverse={isOpen} />
        </ViewSubscriberBenefitsBlock>
        {isOpen && (
          <StoreAvailabilityBlock>
            <StoreAvailability color={colors.white} />
          </StoreAvailabilityBlock>
        )}
      </MobileBenefitsBlock>
    </>
  )
}

const TermsText = styled.p<any>`
  display: flex;
  align-items: center;
    justify-content: center;
  color: ${colors.orange};
  font-family: 'Good Sans Regular';
  font-size: 12px;
  font-style: normal;
  font-weight: normal;
  line-height: 1.5em;
  margin: 0;
  padding: 0;
  text-align: center;
  width: 100%;
  margin-top: 20px;
  a {
      margin-left: 16px;
    border-radius: 5px;
    :hover {
      opacity: 0.5;
    }
  }
`
const ViewSubscriberBenefitsBlock = styled.div<any>`
  display: flex;
  width: 100%;

  align-items: center;

  ${(props: any): any => config(props).media.md`
       justify-content: flex-start;
   `}
`

const StoreAvailabilityBlock = styled.div<any>`
  margin: 12px 0px;
`

const MobileBenefitsBlock = styled.div<any>`
  border-top: 1px solid ${colors.white};
  border-bottom: 1px solid ${colors.white};
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 16px;
`

const SubscribeFeaturesBlock = styled.div<any>`
  margin: 16px 0 0;
`

const Header = styled.h1<any>`
  ${QueensH3};
  text-align: center;
  line-height: 120%;
  span {
    color: ${(props: any): string => props.$theme === 'pink' ? colors.white : colors.tintOrange};
  }
  ${(props: any): any => config(props).media.lg`
    font-size: 60px;
    line-height: 110%;
    margin: 14px 0 0;
  `}
`
const Subheader = styled.p<any>`
  ${GoodSansB1};
  white-space: pre-wrap;
  line-height: 1.58rem;
  margin: 16px 0 26px;
  text-align: center;
  ${(props: any): any => config(props).media.sm`
    font-size: 18px;
    line-height: 1.9rem;
    max-width: 380px;
  `}

  ${(props: any): any => config(props).media.md`
    max-width: 335px;
  `}
  ${(props: any): any => config(props).media.lg`
    max-width: 415px;
  `}
  ${(props: any): any => config(props).media.lg`
    max-width: 515px;
  `}
`

const CombinedHeader = styled.h2<any>`
  font-family: 'Queens Condensed Light';
  font-size: 45px;
  font-weight: 300;
  text-align: center;
  color: ${colors.white};
  margin: 16px 0 24px;
  @media (min-width: 1024px) {
    font-size: 60px;
  }
`

const CombinedLayoutCol = styled(Col)<any>`
  margin: 0 auto;
  ${(props: any): any => config(props).media.md`
  align-items: center;
  max-width: 450px;
`}
  ${(props: any): any => config(props).media.lg`
  max-width: 415px;
`}
  ${(props: any): any => config(props).media.lg`
  max-width: 515px;
`}
`

const CombinedAuthSection = styled.div<any>`
  width: 100%;
  margin-top: -16px;
  margin-bottom: 8px;

  /* Make Create Account submit button filled white */
  .tc-authform-submit {
    background: ${colors.white};
    color: ${colors.midnightBlue};
    border-color: ${colors.white};
    &:hover, &:focus {
      background: ${colors.white};
      color: ${colors.midnightBlue};
      opacity: 0.9;
    }
  }

  /* Make social auth buttons match page background */
  button:not(.tc-authform-submit):not(.tc-authform-toggle-create) {
    background-color: rgba(255, 255, 255, 0.12) !important;
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
    border: 0.5px solid rgba(255, 255, 255, 0.3) !important;
    color: ${colors.white} !important;
  }
`

const GlassPricingCard = styled.div<any>`
  width: 100%;
  margin-bottom: 24px;
`

const CreateAccountDiv = styled.div<any>`
  width: 100%;
  margin-top: 80px;
  margin-bottom: 80px;
`

const StepContainer = styled.div<any>`
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  width: 100%;

  ${(props: any): any => config(props).media.md`
    align-items: start;
    max-width: 450px;
  `}
  ${(props: any): any => config(props).media.lg`
    max-width: 415px;
  `}
  ${(props: any): any => config(props).media.lg`
    max-width: 515px;
  `}
`

const SubscribeCTAButton = styled(CTAPrimaryOrange)<any>`
  ${(props: any): string =>
    props.$buttonColor
      ? `
    background-color: ${props.$buttonColor};
    &:hover {
      background-color: ${props.$buttonColor};
    }
  `
      : ''}
`

const StepHeaderContainer = styled.div<any>`
  margin-bottom: 20px;
  width: 100%;
  h3 {
    text-align: center;
    ${(props: any): any => config(props).media.lg`
      text-align: left;
    `}
  }
`
